import React, { useMemo } from "react";
import { useRecoilState } from "recoil";
import ScreenCard from "../ScreenCard";
import { newMissionState } from "../../atoms/core";
import { CreateMissionScreensContext } from "../../contexts/CreateMissionManager";
import { useI18n } from "../../hooks/i18n-hooks";
import MissionSalary from "../../components/editors/MissionSalary";
import { getMissionPercentage, IjmSelection } from "../../utils";
import { EarningType, Mission } from "../../__generated__/graphql";
import { Salaries } from "../../components/editors/salary/hooks";
import { useKeyboardVisible } from "../../hooks/keyboard";
import { Platform } from "react-native";
import { useBubbleStepOrigin } from "../../hooks/use-bubble-step-origin";
import { Bubble } from "../../components/Bubble";

const CreateMissionSalary = () => {
    const i18n = useI18n();
    const [mission, setMission] = useRecoilState(newMissionState);
    const [loading, setLoading] = React.useState(false);

    const { validateScreen } = React.useContext(CreateMissionScreensContext);
    const keyboardVisible = useKeyboardVisible() && Platform.OS !== "ios";

    const setSalaries = React.useCallback(
        (salaries: Salaries) => {
            setMission((oldState) => ({
                ...oldState,
                currentContract: {
                    ...oldState.currentContract,
                    ...salaries,
                },
            }));
        },
        [setMission],
    );

    const setHas13thSalary = React.useCallback(
        (value: boolean) => {
            setMission((oldState) => ({
                ...oldState,
                currentContract: {
                    ...oldState.currentContract,
                    has13thSalary: value,
                },
            }));
        },
        [setMission],
    );
    const setVacationDays = React.useCallback(
        (value: number) => {
            setMission((oldState) => ({
                ...oldState,
                currentContract: {
                    ...oldState.currentContract,
                    vacationDays: value,
                },
            }));
        },
        [setMission],
    );

    const setTotalHours = React.useCallback(
        (value: number) => {
            setMission((oldState) => ({
                ...oldState,
                currentContract: {
                    ...oldState.currentContract,
                    workingTimeClassicSpec: {
                        ...oldState.currentContract?.workingTimeClassicSpec,
                        unitCount: value,
                    },
                },
            }));
        },
        [setMission],
    );

    const setIjmSelection = React.useCallback(
        (selection: IjmSelection) => {
            setMission((prev) => ({
                ...prev,
                ijmSelection: selection,
            }));
        },
        [setMission],
    );

    const validate = React.useCallback(() => {
        void validateScreen("CreateMissionSalary");
    }, [validateScreen]);

    const isValid = useMemo(() => {
        if (loading) {
            return false;
        }
        if (mission.currentContract?.earningType === EarningType.Package) {
            if (!mission.currentContract?.endDate && !mission.currentContract?.startDate) {
                return false;
            }
            if (!mission.currentContract?.workingTimeClassicSpec?.unitCount) {
                return false;
            }
            if (!mission.ijmSelection?.ijmGroupId) {
                return false;
            }
        }
        return !!mission.currentContract?.grossSalary && !keyboardVisible;
    }, [
        keyboardVisible,
        mission.currentContract?.earningType,
        mission.currentContract?.endDate,
        mission.currentContract?.grossSalary,
        mission.currentContract?.startDate,
        mission.currentContract?.workingTimeClassicSpec?.unitCount,
        mission.ijmSelection?.ijmGroupId,
        loading,
    ]);

    const origin = useBubbleStepOrigin("CreateMissionSalary");

    return (
        <ScreenCard
            buttons={
                !keyboardVisible && [
                    {
                        title: i18n.translate("next"),
                        onPress: validate,
                        isValid,
                    },
                ]
            }
            automaticallyAdjustKeyboardInsets={false}
        >
            <Bubble
                title={
                    mission.currentContract?.earningType
                        ? i18n.t(
                              `screens.user.create_mission_salary_${mission.currentContract.earningType}.title`,
                              {
                                  percent: getMissionPercentage(mission as Mission),
                              },
                          )
                        : ""
                }
                origin={origin}
            >
                <MissionSalary
                    mission={mission}
                    ijmSelection={mission.ijmSelection}
                    initialSalary={mission.currentContract?.grossSalary || 0}
                    setSalaries={setSalaries}
                    setHas13thSalary={setHas13thSalary}
                    setVacationdays={setVacationDays}
                    setTotalHours={setTotalHours}
                    setIjmSelection={setIjmSelection}
                    setLoading={setLoading}
                />
            </Bubble>
        </ScreenCard>
    );
};

export default CreateMissionSalary;
